import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Dropdown, Space } from "antd";
import {
  DownloadOutlined,
  CopyOutlined,
  CheckOutlined,
  QuestionCircleOutlined,
  LinkOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { LogsToolbarProps, WebSocketStatus } from "../types";
import { LogsSettings } from "./LogsSettings";
import { DateRangePicker, RangePreset, WithTimezone } from "../../datepicker";
import { useAppSelector } from "../../../store/hooks";
import { selectLabel as selectTimezoneLabel } from "../../../store/timezoneSlice";
import { IS_LOCAL } from "../../../envVariables";
import "./LogsToolbar.css";

// Default presets for logs time range
const LOGS_PRESETS: RangePreset[] = [
  { label: "Last 5 Minutes", value: ["now-5m", ""] },
  { label: "Last 15 Minutes", value: ["now-15m", ""] },
  { label: "Last 30 Minutes", value: ["now-30m", ""] },
  { label: "Last 1 Hour", value: ["now-1h", ""] },
  { label: "Last 3 Hours", value: ["now-3h", ""] },
  { label: "Last 6 Hours", value: ["now-6h", ""] },
  { label: "Last 12 Hours", value: ["now-12h", ""] },
  { label: "Last 24 Hours", value: ["now-1d", ""] },
  { label: "Last 2 Days", value: ["now-2d", ""] },
  { label: "Last 7 Days", value: ["now-7d", ""] },
  { label: "Last 30 Days", value: ["now-30d", ""] },
];

const LOGQL_DOCS_URL = "https://grafana.com/docs/loki/latest/logql/";

/**
 * Toolbar with query input, time picker, and action buttons
 */
export function LogsToolbar({
  query,
  onQueryChange,
  from,
  to,
  onFromChange,
  onToChange,
  onExecute,
  onCancel,
  onLive,
  onStopLive,
  onRestartLive,
  isLoading,
  isLive,
  isLiveDirty,
  liveStatus,
  onDownload,
  onCopy,
  onOpenGrafana,
  onReset,
  disabled = false,
  filterSlot,
  settings,
  onSettingsChange,
  hasUniqueLabels,
  onQueryFocus,
  onQueryBlur,
  logsEndpoint,
  org,
}: LogsToolbarProps & {
  onCopy: () => void;
  settings: {
    showTimestamp: boolean;
    showLabels: boolean;
    wrapLines: boolean;
    prettifyJSON: boolean;
    orderBy: "asc" | "desc";
  };
  onSettingsChange: (s: Partial<typeof settings>) => void;
  hasUniqueLabels: boolean;
  logsEndpoint?: string;
  org?: string;
}) {
  const timezoneLabel = useAppSelector(selectTimezoneLabel);
  const [isCopied, setIsCopied] = useState(false);
  const [testWs, setTestWs] = useState<WebSocket | null>(null);

  // Reset copy feedback after delay
  useEffect(() => {
    if (!isCopied) return;
    const timeoutId = setTimeout(() => setIsCopied(false), 1500);
    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  // Handle copy with feedback
  const handleCopy = useCallback(async () => {
    await onCopy();
    setIsCopied(true);
  }, [onCopy]);

  // Debug function to test invalid auth (local only)
  const testInvalidAuth = useCallback(() => {
    if (!logsEndpoint || !org) return;
    const baseUrl = logsEndpoint.replace("https://", "wss://");
    const url = `${baseUrl}/logs/org/${org}/loki/api/v1/tail?limit=1000&query={}`;
    const ws = new WebSocket(url);
    ws.onopen = () => {
      ws.send(JSON.stringify({ auth: "invalid-token-for-testing" }));
    };
  }, [logsEndpoint, org]);

  // Debug function to test connection without auth (toggle open/close, local only)
  const testNoAuth = useCallback(() => {
    if (testWs) {
      testWs.close();
      setTestWs(null);
      return;
    }

    if (!logsEndpoint || !org) return;
    const baseUrl = logsEndpoint.replace("https://", "wss://");
    const url = `${baseUrl}/logs/org/${org}/loki/api/v1/tail?limit=1000&query={}`;
    const ws = new WebSocket(url);
    ws.onclose = () => {
      setTestWs(null);
    };
    setTestWs(ws);
  }, [logsEndpoint, org, testWs]);

  // Handle query key press (Shift+Enter to execute)
  const handleQueryKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        onExecute();
      }
    },
    [onExecute],
  );

  // Global keyboard shortcuts: Shift+Enter to execute, Escape to cancel
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && e.shiftKey && !disabled && !isLive) {
        // Don't interfere if user is in a text input (they can use the local handler)
        const target = e.target as HTMLElement;
        const isInTextInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA";
        if (!isInTextInput) {
          e.preventDefault();
          onExecute();
        }
      }
      if (e.key === "Escape" && isLoading && !isLive) {
        e.preventDefault();
        onCancel();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [disabled, isLive, isLoading, onExecute, onCancel]);

  // Download menu items
  const downloadMenuItems = [
    {
      key: "json",
      label: "Download as JSON",
      onClick: () => onDownload("json"),
    },
    {
      key: "txt",
      label: "Download as TXT",
      onClick: () => onDownload("txt"),
    },
  ];

  // Render custom live button based on state
  const renderLiveButton = () => {
    if (disabled) {
      return (
        <div className="logs-live-btn logs-live-btn--disabled">
          <span className="logs-live-dot" />
          <span style={{ marginLeft: 6 }}>Live</span>
        </div>
      );
    }

    if (!isLive) {
      // Idle state - show "Live" button to start
      return (
        <div className="logs-live-btn logs-live-btn--idle" onClick={onLive}>
          <span className="logs-live-dot" />
          <span style={{ marginLeft: 6 }}>Live</span>
        </div>
      );
    }

    if (isLiveDirty) {
      // Dirty state - split button with Restart and Stop
      return (
        <div className="logs-live-btn logs-live-btn--split">
          <div className="logs-live-btn-restart" onClick={onRestartLive}>
            <span className="logs-live-dot logs-live-dot--pulse" />
            <span>Restart</span>
          </div>
          <div className="logs-live-btn-stop-icon" onClick={onStopLive} title="Stop">
            <span className="logs-live-stop-square" />
          </div>
        </div>
      );
    }

    // Live and clean - show "Stop" button with connection status
    const isConnecting = liveStatus === "connecting";
    return (
      <div
        className={`logs-live-btn logs-live-btn--stop ${isConnecting ? "logs-live-btn--connecting" : ""}`}
        onClick={onStopLive}
      >
        <span className={`logs-live-dot ${isConnecting ? "" : "logs-live-dot--pulse"}`} />
        <span style={{ marginLeft: 6 }}>{isConnecting ? "Connecting..." : "Stop"}</span>
      </div>
    );
  };

  return (
    <div className="logs-toolbar">
      {/* Query input */}
      <div className="logs-toolbar-row">
        <div className="logs-toolbar-query-wrapper">
          <label className="logs-toolbar-label">Query</label>
          <Input.TextArea
            className="logs-toolbar-query"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={handleQueryKeyDown}
            onFocus={onQueryFocus}
            onBlur={onQueryBlur}
            placeholder='{app="nginx"} |= "error"'
            autoSize={{ minRows: 1, maxRows: 3 }}
            disabled={disabled}
          />
        </div>
        <div className="logs-toolbar-query-actions">
          <a
            href={LOGQL_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="logs-toolbar-link"
            title="LogQL Documentation"
          >
            LogQL Docs <QuestionCircleOutlined />
          </a>
          <span className="logs-toolbar-hint">{isLoading ? "Escape to cancel" : "Shift+Enter to execute"}</span>
        </div>
      </div>

      {/* Time picker and filters */}
      <div className="logs-toolbar-row">
        <div className="logs-toolbar-field">
          <label className="logs-toolbar-label">Time Range ({timezoneLabel})</label>
          <WithTimezone>
            <DateRangePicker
              from={from}
              to={to}
              setFrom={onFromChange}
              setTo={onToChange}
              disabled={disabled}
              granularity="second"
              presets={LOGS_PRESETS}
            />
          </WithTimezone>
        </div>

        {filterSlot && <div className="logs-toolbar-filters">{filterSlot}</div>}
      </div>

      {/* Action buttons */}
      <div className="logs-toolbar-row logs-toolbar-actions">
        <Space size="middle">
          <LogsSettings settings={settings} onChange={onSettingsChange} hasUniqueLabels={hasUniqueLabels} />
        </Space>

        <Space size="middle">
          <a onClick={onOpenGrafana} className="logs-toolbar-link logs-toolbar-grafana-link">
            Explore on Grafana <LinkOutlined />
          </a>

          <Button
            icon={isCopied ? <CheckOutlined style={{ color: "var(--color-action)" }} /> : <CopyOutlined />}
            onClick={handleCopy}
            disabled={disabled}
            title="Copy all logs as text"
          />
          <Dropdown menu={{ items: downloadMenuItems }} disabled={disabled}>
            <Button icon={<DownloadOutlined />} title="Download logs" />
          </Dropdown>

          <Button
            icon={<UndoOutlined />}
            onClick={onReset}
            disabled={disabled || isLive}
            title="Reset query to default"
          >
            Reset
          </Button>

          {IS_LOCAL && (
            <>
              <Button onClick={testInvalidAuth} danger title="Test invalid auth (debug)">
                Test Invalid Auth
              </Button>

              <Button onClick={testNoAuth} danger title="Test no auth - click to open, click again to close">
                {testWs ? "Close Test WS" : "Test No Auth"}
              </Button>
            </>
          )}

          {isLoading ? (
            <Button danger onClick={onCancel}>
              Cancel
            </Button>
          ) : (
            <Button type="primary" onClick={onExecute} disabled={disabled || isLive}>
              Query
            </Button>
          )}

          {renderLiveButton()}
        </Space>
      </div>
    </div>
  );
}
